import { Skeleton } from '@/components/ui/skeleton'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

interface LoadingStateProps {
  variant?: 'page' | 'card' | 'table' | 'inline'
  /** For table variant — number of skeleton rows (default 5) */
  rows?: number
  className?: string
}

/**
 * LoadingState — consistent loading UI across the app.
 *
 * Variants:
 * - page: full viewport spinner (used during lazy-load Suspense)
 * - card: 3-line skeleton (fits inside a Card)
 * - table: skeleton rows (matches data table layout)
 * - inline: small spinner + "Loading…" text
 */
export function LoadingState({ variant = 'inline', rows = 5, className }: LoadingStateProps) {
  if (variant === 'page') {
    return (
      <div className={cn('flex items-center justify-center h-full min-h-64', className)}>
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (variant === 'card') {
    return (
      <div className={cn('space-y-3', className)}>
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-4/5" />
      </div>
    )
  }

  if (variant === 'table') {
    return (
      <div className={cn('space-y-2', className)}>
        {Array.from({ length: rows }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    )
  }

  // inline
  return (
    <span className={cn('inline-flex items-center gap-2 text-sm text-muted-foreground', className)}>
      <Loader2 className="h-4 w-4 animate-spin" />
      Loading…
    </span>
  )
}

/** Full-page loading screen used by Suspense fallback in router */
export function LoadingPage() {
  return <LoadingState variant="page" />
}
